// Iterate voxel positions along a single axis from anchor to end (inclusive)
export function* voxelLineAlongAxis(
  anchor: { x: number; y: number; z: number },
  end: { x: number; y: number; z: number },
  axis: 'x' | 'y' | 'z'
): Generator<{ x: number; y: number; z: number }> {
  const start = anchor[axis]
  const finish = end[axis]
  const step = start <= finish ? 1 : -1
  for (let i = start; step > 0 ? i <= finish : i >= finish; i += step) {
    const pos = { ...anchor, [axis]: i } as { x: number; y: number; z: number }
    yield pos
  }
}

// Iterate all integer coordinates in an inclusive axis-aligned box
export function forEachInBox(
  min: { x: number; y: number; z: number },
  max: { x: number; y: number; z: number },
  fn: (x: number, y: number, z: number) => void
) {
  for (let x = min.x; x <= max.x; x++)
    for (let y = min.y; y <= max.y; y++)
      for (let z = min.z; z <= max.z; z++) {
        fn(x, y, z)
      }
}

// Moved from helpers/sampling (C26): iterate integer coordinates inside a sphere
export function forEachInSphere(
  cx: number,
  cy: number,
  cz: number,
  r: number,
  fn: (x: number, y: number, z: number, d: number) => void
) {
  const r2 = r * r
  for (let x = Math.floor(cx - r); x <= Math.ceil(cx + r); x++)
    for (let y = Math.floor(cy - r); y <= Math.ceil(cy + r); y++)
      for (let z = Math.floor(cz - r); z <= Math.ceil(cz + r); z++) {
        const dx = x - cx, dy = y - cy, dz = z - cz
        const d2 = dx*dx + dy*dy + dz*dz
        if (d2 <= r2) fn(x, y, z, Math.sqrt(d2))
      }
}

// Iterate voxels that form a hollow sphere shell between inner and outer radii (inclusive).
export function forEachInHollowSphere(
  cx: number,
  cy: number,
  cz: number,
  outerRadius: number,
  innerRadius: number,
  fn: (x: number, y: number, z: number, distance: number) => void
) {
  const outer = Math.max(0, outerRadius)
  if (outer === 0) return
  const outer2 = outer * outer
  const inner = Math.max(0, Math.min(innerRadius, outer))
  const inner2 = inner * inner
  for (let x = Math.floor(cx - outer); x <= Math.ceil(cx + outer); x++)
    for (let y = Math.floor(cy - outer); y <= Math.ceil(cy + outer); y++)
      for (let z = Math.floor(cz - outer); z <= Math.ceil(cz + outer); z++) {
        const dx = x - cx
        const dy = y - cy
        const dz = z - cz
        const d2 = dx * dx + dy * dy + dz * dz
        if (d2 > outer2) continue
        if (inner2 > 0 && d2 < inner2) continue
        fn(x, y, z, Math.sqrt(d2))
      }
}
